/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';

import AppTable from 'components/appTable';
import Empty from 'components/empty';
import SearchBox from 'components/form/searchBox';
import PanelBoxLayout from 'components/layout/panelBox';
import Toolbar, { ToolbarRight } from 'components/toolbar';
import { useActions } from 'common/hooks/useActions';
import RowAction from 'components/rowAction';
import EventFilterPopup from '../filterForm';
import PaginationApp from 'components/pagination';

import * as S from './styles';
import * as T from 'common/types';
import Value from 'components/value';
import { formatDateTime, MISSING } from 'common/helpers/format';

const PenIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);

const NewsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 5h13a2 2 0 0 1 2 2v10a2 2 0 0 0 2 2H6a2 2 0 0 1-2-2z" />
    <path d="M8 9h5M8 13h5" />
  </svg>
);

interface IProps {
  data: { list: []; totalPages?: number };
  loading: boolean;
}

export default function NewsTable({ data, loading }: IProps) {
  const { newsRequestAction } = useActions();
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 10,
    search: '',
    type: ''
  });

  const fetchData = () => {
    newsRequestAction(meta);
  };

  useEffect(() => {
    fetchData();
  }, [meta]);

  const handleChangePage = (page: number) => {
    setMeta({ ...meta, page });
  };

  const handleChangePageSize = (page_size: number) => {
    setMeta({ ...meta, page_size });
  };

  return (
    <S.Content>
      <PanelBoxLayout className="flush" contentStyle={{ padding: 0 }}>
        <Toolbar>
          <SearchBox
            onChange={(e) => setMeta({ ...meta, search: e, page: 1 })}
            placeholder="Search news"
            value={meta.search}
          />
          <ToolbarRight>
            <EventFilterPopup setFilter={setMeta} filter={meta} />
          </ToolbarRight>
        </Toolbar>
        <AppTable
          headers={[
            { id: 1, key: 'id', title: 'ID', dir: 'left', width: 84 },
            { id: 4, key: 'title', title: 'Title', dir: 'left' },
            { id: 5, key: 'reference', title: 'Reference', dir: 'left', width: 200 },
            { id: 8, key: 'createdAt', title: 'Created At', dir: 'left', width: 156 },
            { id: 10, key: 'edit', title: '', type: 'actions', width: 64 }
          ]}
          data={data.list}
          empty={
            meta.search ? (
              <Empty
                title={`No news matches “${meta.search}”`}
                text="Check the spelling, or clear the search to see every article."
              />
            ) : (
              <Empty icon={<NewsIcon />} title="No news yet" text="Publish an article and it appears here." />
            )
          }
          loading={loading}
          altanfeethiFields={{
            id: (item: T.Pages.NewsListItem) => (
              <S.Div className="text-left">
                <Value mono>{item?.id}</Value>
              </S.Div>
            ),

            /* Both of these cut at 50 characters in JS; AppTable clips what
               does not fit and puts the whole value on the cell's title. */
            title: (item: T.Pages.NewsListItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.provided}>
                  {item?.newsTranslations?.find((t) => t.language === 'en')?.title}
                </Value>
              </S.Div>
            ),

            reference: (item: T.Pages.NewsListItem) => (
              <S.Div className="text-left">
                <Value fallback={MISSING.none}>{item?.reference}</Value>
              </S.Div>
            ),
            createdAt: (item: T.Pages.NewsListItem) => (
              <S.Div className="text-left">
                <Value mono>{formatDateTime(item?.createdAt)}</Value>
              </S.Div>
            ),
            edit: (item: T.Pages.NewsListItem) => (
              <RowAction to={`/panel/news/${item.id}`} label="Edit article">
                <PenIcon />
              </RowAction>
            )
          }}
        />
      </PanelBoxLayout>
      {data.totalPages ? (
        <PaginationApp
          page={meta.page}
          count={meta.page_size * data.totalPages}
          handleChangePage={(page: number) => handleChangePage(page)}
          key={data.totalPages}
          pageSize={meta.page_size}
          handleChangeTake={handleChangePageSize}
        />
      ) : (
        <></>
      )}
    </S.Content>
  );
}
